/**
 * useAccountBalancesReport Hook
 *
 * A React hook for fetching account balances report data from the API.
 * Provides account balance history for line chart visualization of trends.
 *
 * @see docs/plans/iterations/transaction-reporting/contracts/frontend-rails-api.json
 */

import { useState, useCallback } from 'react';
import type { AccountBalancesReportResponse, ReportGranularity } from '@/types/accounting';

/**
 * Return type for the useAccountBalancesReport hook
 */
export interface UseAccountBalancesReportReturn {
  /** Current account balances report data */
  data: AccountBalancesReportResponse | null;
  /** Whether an API request is in progress */
  loading: boolean;
  /** Error message from the last failed request */
  error: string | null;
  /** Fetch account balances report with current parameters */
  fetchReport: () => Promise<AccountBalancesReportResponse>;
  /** Clear any error state */
  clearError: () => void;
}

/**
 * Hook for fetching account balances report
 *
 * @param startDate - Start date of the report period (ISO date string: "2024-01-01")
 * @param endDate - End date of the report period (ISO date string: "2024-12-31")
 * @param granularity - Time granularity for balance snapshots ("monthly", "quarterly", or "yearly")
 * @returns Object containing data, loading state, error, and fetch function
 *
 * @example
 * ```typescript
 * const { data, loading, error, fetchReport } = useAccountBalancesReport(
 *   '2024-01-01',
 *   '2024-12-31',
 *   'monthly'
 * );
 *
 * // Fetch report on mount
 * useEffect(() => {
 *   fetchReport();
 * }, [fetchReport]);
 *
 * // Display account balances
 * if (data) {
 *   data.accounts.forEach(account => {
 *     console.log(`${account.accountName}: ${account.currentBalanceCents} cents`);
 *   });
 *   console.log(`Net Worth: ${data.netWorth.currentCents} cents`);
 * }
 * ```
 */
export function useAccountBalancesReport(
  startDate: string,
  endDate: string,
  granularity: ReportGranularity
): UseAccountBalancesReportReturn {
  const [data, setData] = useState<AccountBalancesReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch account balances report from the API
   */
  const fetchReport = useCallback(async (): Promise<AccountBalancesReportResponse> => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        granularity: granularity,
      });

      const response = await fetch(`/api/accounting/reports/account_balances?${params}`);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to fetch report: ${response.statusText}`);
      }

      const json = (await response.json()) as AccountBalancesReportResponse;
      setData(json);
      return json;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load account balances report';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, granularity]);

  return { data, loading, error, fetchReport, clearError };
}
